import React, { useEffect, useState, useMemo } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Download, X } from "lucide-react";

export default function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [stores, setStores] = useState([]);
    const [filters, setFilters] = useState({
        store_id: "",
        status: "",
        date_from: "",
        date_to: "",
    });

    const refresh = async () => {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
        const { data } = await api.get("/admin/bookings", { params });
        const sorted = [...data].sort(
            (a, b) =>
                (b.slot_date + b.slot_time).localeCompare(a.slot_date + a.slot_time),
        );
        setBookings(sorted);
    };

    useEffect(() => {
        api.get("/admin/stores").then((r) => setStores(r.data));
    }, []);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const cancel = async (b) => {
        if (!window.confirm(`Annullare la prenotazione di ${b.first_name} ${b.last_name}?`)) return;
        try {
            await api.post(`/admin/bookings/${b.id}/cancel`);
            toast.success("Prenotazione annullata. Email inviata al cliente.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const exportCsv = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        const token = localStorage.getItem("magni_admin_token");
        // Use fetch to include header, then download as blob
        fetch(`${API}/admin/bookings/export.csv?${params.toString()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "prenotazioni.csv";
                a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <div>
            <Toaster position="top-center" richColors />
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="overline mb-2">Backoffice</div>
                    <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                        Prenotazioni
                    </h1>
                </div>
                <button onClick={exportCsv} data-testid="export-csv-btn" className="magni-btn-secondary">
                    <Download className="w-4 h-4 mr-2" /> Esporta CSV
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white border border-magni-light p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        Negozio
                    </label>
                    <select
                        data-testid="filter-store"
                        value={filters.store_id}
                        onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                        className="magni-input"
                    >
                        <option value="">Tutti i negozi</option>
                        {stores.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        Stato
                    </label>
                    <select
                        data-testid="filter-status"
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="magni-input"
                    >
                        <option value="">Tutti</option>
                        <option value="confirmed">Confermate</option>
                        <option value="checked_in">Cliente in store</option>
                        <option value="completed">Completate</option>
                        <option value="cancelled">Annullate</option>
                        <option value="no_show">No-show</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        Da
                    </label>
                    <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="magni-input"
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                        A
                    </label>
                    <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="magni-input"
                    />
                </div>
            </div>

            <div className="bg-white border border-magni-light overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-magni-cream text-magni-muted">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Data</th>
                            <th className="text-left px-4 py-3 font-medium">Ora</th>
                            <th className="text-left px-4 py-3 font-medium">Cliente</th>
                            <th className="text-left px-4 py-3 font-medium">Contatti</th>
                            <th className="text-left px-4 py-3 font-medium">Negozio</th>
                            <th className="text-left px-4 py-3 font-medium">Note</th>
                            <th className="text-left px-4 py-3 font-medium">Stato</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookings.length === 0 && (
                            <tr>
                                <td colSpan="8" className="px-6 py-10 text-center text-magni-muted">
                                    Nessuna prenotazione con i filtri selezionati.
                                </td>
                            </tr>
                        )}
                            {bookings.map((b) => {
                                const eff = b.effective_status || b.status;
                                const cfg = {
                                    confirmed: { dot: "bg-magni-navy", cls: "bg-magni-celeste text-magni-navy", label: "Confermata" },
                                    checked_in: { dot: "bg-green-500", cls: "bg-green-50 text-green-700", label: "In store" },
                                    completed: { dot: "bg-green-700", cls: "bg-green-50 text-green-700", label: b.purchased ? "Acquisto" : "No acquisto" },
                                    cancelled: { dot: "bg-red-500", cls: "bg-red-50 text-red-700", label: "Annullata" },
                                    no_show: { dot: "bg-red-500", cls: "bg-red-50 text-red-700", label: "No show" },
                                }[eff] || { dot: "bg-magni-light", cls: "bg-magni-light text-magni-muted", label: eff };
                                return (
                                <tr key={b.id} className="border-t border-magni-light" data-testid={`booking-row-${b.id}`}>
                                    <td className="px-4 py-3 text-magni-navy">{b.slot_date}</td>
                                    <td className="px-4 py-3 text-magni-navy">{b.slot_time}</td>
                                    <td className="px-4 py-3 text-magni-navy font-medium">
                                        {b.first_name} {b.last_name}
                                    </td>
                                    <td className="px-4 py-3 text-magni-muted text-xs">
                                        {b.email}
                                        <br />
                                        {b.phone}
                                    </td>
                                    <td className="px-4 py-3 text-magni-muted">{b.store_name}</td>
                                    <td className="px-4 py-3 text-magni-muted text-xs max-w-[200px] truncate" title={b.notes}>
                                        {b.notes || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1.5 ${cfg.cls}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
                                            {cfg.label}
                                        </span>
                                        {b.status === "completed" && (
                                            <div className="text-[11px] text-magni-muted mt-1">
                                                Acquisto: <strong className={b.purchased ? "text-green-700" : "text-magni-navy"}>{b.purchased ? "Sì" : "No"}</strong>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {b.status === "confirmed" && (
                                            <button
                                                data-testid={`cancel-booking-${b.id}`}
                                                onClick={() => cancel(b)}
                                                className="text-red-600 hover:text-red-800 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1"
                                            >
                                                <X className="w-3 h-3" /> Annulla
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            <div className="text-xs text-magni-muted mt-3" data-testid="bookings-count">
                {bookings.length} prenotazioni
            </div>
        </div>
    );
}
